/**
 * useUIStore — Zustand store for all UI-related state
 *
 * Manages: active tab, modal visibility, toasts, offline status, theme.
 * This eliminates ~15 useState calls from App.tsx and makes UI state
 * accessible from any component without prop drilling.
 */
import { create } from 'zustand';
import type { ToastItem, ToastType } from '../components/Toast';

export type TabId = 'learn' | 'exam' | 'chat' | 'profile' | 'leaderboard' | 'admin';
export type LegalModalType = 'impressum' | 'datenschutz' | 'agb' | 'cookies' | null;
export type PremiumTrigger = 'chapter' | 'exam' | 'ai' | 'general' | 'preview' | null;

/** Optional dynamic context for the premium modal copy (e.g. preview paywall). */
export interface PremiumContext {
  chapterId?: number;
  freeCount?: number;
  totalCount?: number;
}

const TAB_ORDER: TabId[] = ['learn', 'exam', 'leaderboard', 'chat', 'profile'];

// ── Haptic Feedback Utility ────────────────────────────
const HAPTIC_PATTERNS = {
  tap: [10],
  success: [50],
  error: [100, 50, 100],
  celebrate: [50, 30, 50, 30, 100],
  micro: [5],
} as const;

export type HapticPattern = keyof typeof HAPTIC_PATTERNS;

/** Trigger haptic feedback if supported. Safe no-op on desktop. */
export function haptic(pattern: HapticPattern = 'tap') {
  if (navigator.vibrate) navigator.vibrate(HAPTIC_PATTERNS[pattern]);
}

interface UIState {
  // Navigation
  activeTab: TabId;
  tabDirection: 'left' | 'right';

  // Modals
  showPaywall: boolean;
  showOnboarding: boolean;
  showBetaFeedback: boolean;
  showConfidenceCheck: boolean;
  showPremiumCelebration: boolean;
  legalModalType: LegalModalType;
  premiumTrigger: PremiumTrigger;
  premiumContext: PremiumContext | null;

  // Toasts (stackable)
  toasts: ToastItem[];
  showUpdateToast: boolean;
  // F-20: SW update prompt. When a new build is detected and no exam
  // session is active, show a sticky banner with a manual "Update now"
  // button instead of silently reloading. updateApply() is the callback
  // bound to vite-plugin-pwa's updateSW(true) from index.tsx.
  updatePromptVisible: boolean;
  updateApply: (() => void) | null;

  // App State
  isOffline: boolean;
  isLoadingQuestions: boolean;
  authLoading: boolean;
  maintenanceMode: boolean;

  // Beta
  showBetaBadge: boolean;
  betaBannerMeta: { banner_title?: string; banner_badge?: string; banner_subtitle?: string } | null;

  // Actions — Navigation
  setActiveTab: (tab: TabId) => void;

  // Actions — Modals
  openPaywall: () => void;
  closePaywall: () => void;
  setShowOnboarding: (show: boolean) => void;
  setShowBetaFeedback: (show: boolean) => void;
  setShowConfidenceCheck: (show: boolean) => void;
  setShowPremiumCelebration: (show: boolean) => void;
  setLegalModalType: (type: LegalModalType) => void;
  setPremiumTrigger: (trigger: PremiumTrigger, context?: PremiumContext | null) => void;
  setPremiumContext: (context: PremiumContext | null) => void;

  // Actions — Toasts
  showToast: (message: string, durationMs?: number, type?: ToastType) => void;
  dismissToast: (id: string) => void;
  setShowUpdateToast: (show: boolean) => void;
  setUpdatePrompt: (visible: boolean, apply?: (() => void) | null) => void;

  // Actions — App State
  setIsOffline: (offline: boolean) => void;
  setIsLoadingQuestions: (loading: boolean) => void;
  setAuthLoading: (loading: boolean) => void;
  setMaintenanceMode: (enabled: boolean) => void;

  // Actions — Beta
  setShowBetaBadge: (show: boolean) => void;
  setBetaBannerMeta: (meta: UIState['betaBannerMeta']) => void;
}

let toastCounter = 0;

export const useUIStore = create<UIState>((set, get) => ({
  // ── Initial State ──────────────────────────
  activeTab: 'learn',
  tabDirection: 'right',

  showPaywall: false,
  showOnboarding: false,
  showBetaFeedback: false,
  showConfidenceCheck: false,
  showPremiumCelebration: false,
  legalModalType: null,
  premiumTrigger: null,
  premiumContext: null,

  toasts: [],
  showUpdateToast: false,
  updatePromptVisible: false,
  updateApply: null,

  isOffline: !navigator.onLine,
  isLoadingQuestions: false,
  authLoading:
    window.location.hash.includes('access_token=') ||
    new URLSearchParams(window.location.search).has('code'),
  maintenanceMode: false,

  showBetaBadge: false,
  betaBannerMeta: null,

  // ── Actions ────────────────────────────────
  setActiveTab: (tab) => {
    const { activeTab: currentTab } = get();
    if (tab === currentTab) return;
    const oldIndex = TAB_ORDER.indexOf(currentTab);
    const newIndex = TAB_ORDER.indexOf(tab);
    const doSwitch = () =>
      set({
        activeTab: tab,
        tabDirection: newIndex >= oldIndex ? 'right' : 'left',
      });
    // View Transitions API (Chrome 111+)
    if ('startViewTransition' in document) {
      (document as any).startViewTransition(doSwitch);
    } else {
      doSwitch();
    }
    haptic('tap');
  },

  openPaywall: () => set({ showPaywall: true }),
  closePaywall: () => set({ showPaywall: false }),
  setShowOnboarding: (show) => set({ showOnboarding: show }),
  setShowBetaFeedback: (show) => set({ showBetaFeedback: show }),
  setShowConfidenceCheck: (show) => set({ showConfidenceCheck: show }),
  setShowPremiumCelebration: (show) => set({ showPremiumCelebration: show }),
  setLegalModalType: (type) => set({ legalModalType: type }),
  setPremiumTrigger: (trigger, context = null) =>
    set({ premiumTrigger: trigger, premiumContext: trigger ? context : null }),
  setPremiumContext: (context) => set({ premiumContext: context }),

  showToast: (message, durationMs = 3000, type: ToastType = 'success') => {
    const id = `toast-${++toastCounter}`;
    const toast: ToastItem = { id, message, type, duration: durationMs, createdAt: Date.now() };
    set((state) => ({ toasts: [...state.toasts.slice(-2), toast] })); // max 3
  },
  dismissToast: (id) => {
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
  },
  setShowUpdateToast: (show) => set({ showUpdateToast: show }),
  setUpdatePrompt: (visible, apply = null) =>
    set({ updatePromptVisible: visible, updateApply: apply }),

  setIsOffline: (offline) => set({ isOffline: offline }),
  setIsLoadingQuestions: (loading) => set({ isLoadingQuestions: loading }),
  setAuthLoading: (loading) => set({ authLoading: loading }),
  setMaintenanceMode: (enabled) => set({ maintenanceMode: enabled }),

  setShowBetaBadge: (show) => set({ showBetaBadge: show }),
  setBetaBannerMeta: (meta) => set({ betaBannerMeta: meta }),
}));
